import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* 她按住说话（2026-10-08 铃点单）：输入框右边一个细线条小麦克风，按住录、松手发、上滑取消。
   录好的音频交给大脑 /api/voice/upload（存下来 + Groq 转文字），回来的 {id, text}
   由 Chat 当成一条她的语音消息发出去。浮层用 portal 挂到 body（聊天外壳里有 transform，
   fixed 会跑位）。最长 60 秒，太短（<0.8 秒）不发。 */

const MAX_SEC = 60;
const CANCEL_DY = 70; // 手指往上滑超过这么多 px = 松手取消

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  const cands = [
    ['audio/mp4', 'm4a'],
    ['audio/webm;codecs=opus', 'webm'],
    ['audio/webm', 'webm'],
  ];
  for (const [mt, ext] of cands) {
    try { if (MediaRecorder.isTypeSupported(mt)) return { mt, ext }; } catch {}
  }
  return null;
}

export default function VoiceRecorder({ disabled, onVoice }) {
  const [phase, setPhase] = useState('idle'); // idle | rec | sending
  const [cancel, setCancel] = useState(false);
  const [sec, setSec] = useState(0);
  const [tip, setTip] = useState('');
  const st = useRef({});

  const flashTip = (t) => {
    setTip(t);
    clearTimeout(st.current.tipTimer);
    st.current.tipTimer = setTimeout(() => setTip(''), 1800);
  };

  const cleanup = () => {
    clearInterval(st.current.tick);
    try { st.current.stream?.getTracks().forEach((t) => t.stop()); } catch {}
    st.current.stream = null;
    st.current.rec = null;
  };
  useEffect(() => () => { cleanup(); clearTimeout(st.current.tipTimer); }, []);

  const finish = (send) => {
    const s = st.current;
    if (!s.rec) { s.down = false; cleanup(); setPhase('idle'); return; }
    s.send = send;
    try { s.rec.stop(); } catch { cleanup(); setPhase('idle'); }
  };

  const onDown = async (e) => {
    if (disabled || phase !== 'idle') return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
    const s = st.current;
    s.down = true;
    s.y0 = e.clientY;
    setCancel(false);
    const mime = pickMime();
    if (!mime || !navigator.mediaDevices?.getUserMedia) { flashTip('这台设备还不能录音'); return; }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      flashTip('要先允许用麦克风哦');
      return;
    }
    // 第一次弹「允许麦克风」时手指早松开了 → 这次只拿权限，不录
    if (!s.down) { stream.getTracks().forEach((t) => t.stop()); flashTip('好啦，再按住说话吧'); return; }
    const chunks = [];
    let rec;
    try { rec = new MediaRecorder(stream, { mimeType: mime.mt }); } catch { rec = new MediaRecorder(stream); }
    s.stream = stream;
    s.rec = rec;
    s.ext = mime.ext;
    s.t0 = Date.now();
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
    rec.onstop = async () => {
      const dur = (Date.now() - s.t0) / 1000;
      const send = s.send;
      cleanup();
      if (!send) { setPhase('idle'); return; }
      if (dur < 0.8) { setPhase('idle'); flashTip('太短啦，按住多说一会儿'); return; }
      setPhase('sending');
      try {
        const blob = new Blob(chunks, { type: rec.mimeType || mime.mt });
        const r = await fetch('/api/voice/upload?ext=' + s.ext, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': blob.type || 'application/octet-stream' },
          body: blob,
        });
        if (!r.ok) throw new Error(String(r.status));
        const d = await r.json();
        onVoice({ id: d.id, text: d.text || '', tone: d.tone || '', dur: Math.round(dur * 10) / 10 });
      } catch {
        flashTip('没发出去，网络打了个盹，再试一次？');
      }
      setPhase('idle');
    };
    rec.start();
    setSec(0);
    setPhase('rec');
    try { navigator.vibrate?.(10); } catch {}
    s.tick = setInterval(() => {
      const t = Math.floor((Date.now() - s.t0) / 1000);
      setSec(t);
      if (t >= MAX_SEC) { s.down = false; finish(true); }
    }, 250);
  };

  const onMove = (e) => {
    const s = st.current;
    if (!s.down || !s.rec) return;
    setCancel(s.y0 - e.clientY > CANCEL_DY);
  };
  const onUp = (e) => {
    const s = st.current;
    if (!s.down) return;
    s.down = false;
    const cancelNow = s.y0 - e.clientY > CANCEL_DY;
    finish(!cancelNow);
  };
  const onCancelPtr = () => {
    const s = st.current;
    if (!s.down) return;
    s.down = false;
    finish(false);
  };

  const mm = Math.floor(sec / 60);
  const ss = String(sec % 60).padStart(2, '0');
  return (
    <>
      <button
        type="button"
        className={'ch-mic' + (phase !== 'idle' ? ' on' : '')}
        disabled={disabled}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onCancelPtr}
        onContextMenu={(e) => e.preventDefault()}
        aria-label="按住说话"
        title="按住说话"
      >
        <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" fill="none"
             stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="3" width="6" height="11" rx="3" />
          <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
          <path d="M12 18v3" />
        </svg>
      </button>
      {(phase !== 'idle' || tip) && createPortal(
        <div className={'ch-rec-pop' + (cancel ? ' cancel' : '')} role="status" aria-live="polite">
          {phase === 'rec' && (
            <>
              <span className="ch-rec-dot" aria-hidden="true" />
              <span className="ch-rec-time">{mm}:{ss}</span>
              <span className="ch-rec-hint">{cancel ? '松手取消' : '松手发送 · 上滑取消'}</span>
            </>
          )}
          {phase === 'sending' && <span className="ch-rec-hint">发送中…</span>}
          {phase === 'idle' && tip && <span className="ch-rec-hint">{tip}</span>}
        </div>,
        document.body,
      )}
    </>
  );
}
